import React from "react";
import { useExtensionRegistry } from "@core/components/PipelineComponents/ExtensionProvider";

/**
 * @typedef {Object} ExtensionPointProps
 * @property {string} name - The name of the extension point.
 * @property {Record<string, any>} [props] - Additional props.
 */
interface ExtensionPointProps extends Record<string, any> {
  name: string;
}

/**
 * ExtensionPoint component.
 * 
 * This component renders all registered extensions for a given extension point.
 * This is intended to render components from different plugins.
 * 
 * @component
 * @example
 * Example usage:
 * <ExtensionPoint name="header" someProp="value" />
 * 
 * @param {ExtensionPointProps} props - The props for the components.
 * @returns {React.ReactElement} The rendered components.
 */
const ExtensionPoint: React.FC<ExtensionPointProps> = ({ name, ...props }) => {
  const { getExtensions } = useExtensionRegistry();
  const extensions = getExtensions(name);

  return (
    <>
      {extensions.map((ExtensionComponent, index) => (
        <ExtensionComponent key={index} {...props} />
      ))}
    </>
  );
};

export default ExtensionPoint;
